import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import type { LoroDoc } from "loro-crdt";
import { Button } from "../ui";
import { doneBetween } from "../core/doneOn";
import type { Task } from "../core/schema";
import { useDocSelector } from "../core/useDocSelector";
import type { DocHandle } from "../sync/docManager";
import { movePlan, readDay } from "../planner/ops";

const heading = "mb-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500";

/** The Worklog's two tabs (UI cleanup U4): the day, and the export. */
export function WorklogTabs() {
  const { t } = useTranslation();
  const tab = ({ isActive }: { isActive: boolean }) =>
    `rounded-sm px-3 py-1 ${isActive ? "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100" : "text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"}`;
  return (
    <nav
      className="flex rounded-md border border-neutral-200 bg-[var(--surface)] p-0.5 text-[13px] dark:border-neutral-800"
      aria-label={t("worklog.tabs.label")}
    >
      <NavLink to="/worklog" end className={tab} data-testid="worklog-tab-day">
        {t("worklog.tabs.day")}
      </NavLink>
      <NavLink to="/worklog/export" className={tab} data-testid="worklog-tab-export">
        {t("worklog.tabs.export")}
      </NavLink>
    </nav>
  );
}

/**
 * The day's review (spec §9.6, §9.19 done-list; UI cleanup U4, which replaced the Summary page): what got done, by
 * the one rule (`doneBetween`, done repeats included), and, for today, what was planned and is still open, each
 * with *Move to tomorrow*. No open-task count unless `settings.summary.showOpenCount`.
 */
export function DayDone({
  core,
  tasks,
  day,
  isToday,
  showOpenCount,
  openTask,
}: {
  core: DocHandle | null;
  tasks: Task[];
  day: string;
  isToday: boolean;
  showOpenCount: boolean;
  openTask(id: string): void;
}) {
  const { t } = useTranslation();
  const plan = useDocSelector(core, (doc) => readDay(doc, day), [day]) ?? [];
  const { done, openCount } = useMemo(() => {
    const start = new Date(day + "T00:00:00");
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return {
      done: tasks.filter((x) => doneBetween(x, start.getTime(), end.getTime())),
      openCount: tasks.filter((x) => !x.doneAt && !x.archivedAt && x.typeId === "task").length,
    };
  }, [tasks, day]);
  const byId = new Map(tasks.map((x) => [x.id, x]));
  const leftover = isToday ? plan.filter((p) => byId.get(p.taskId) && !byId.get(p.taskId)!.doneAt) : [];
  const tomorrow = (() => {
    const d = new Date(day + "T12:00:00");
    d.setDate(d.getDate() + 1);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  })();
  const write = (fn: (doc: LoroDoc) => void) => core && !core.readOnly && fn(core.doc);

  return (
    <>
      <section className="mb-6" data-testid="day-done">
        <h2 className={heading}>{t("day.done")}</h2>
        {done.length === 0 ? (
          <p className="text-sm text-neutral-500">{t("day.nothingDone")}</p>
        ) : (
          <ul className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800">
            {done.map((x) => (
              <li key={x.id} className="px-4 py-2 text-sm">
                <button type="button" className="text-left hover:text-accent" onClick={() => openTask(x.id)}>
                  {x.title}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      {leftover.length > 0 && (
        <section className="mb-6" data-testid="day-carry">
          <h2 className={heading}>{t("day.carryOver")}</h2>
          <ul className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800">
            {leftover.map((p) => (
              <li key={p.taskId} className="flex items-center gap-3 px-4 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{byId.get(p.taskId)?.title}</span>
                {core && !core.readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => write((doc) => movePlan(doc, day, tomorrow, p.taskId))}
                    data-testid="day-move-tomorrow"
                  >
                    {t("day.moveToTomorrow")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {showOpenCount && (
        <p className="mb-6 text-[12.5px] text-neutral-500">{t("day.openCount", { n: openCount })}</p>
      )}
    </>
  );
}
